type FilterData = {
  locations: { [key: string]: { [key: number]: string } }
  conditions: Array<{ id: number; name: string }>
}

type ItemSearchQueries = {
  categoryJson?: string
  itemSearchKeywords?: string
  categories?: string[]
  locations?: string[]
  itemConditionIds?: string[]
  pickupDates?: string[]
  auctionEndDates?: string[]
  pageId?: number | string
}

type ItemSearchGtmData = {
  searchKeyword?: string
  locations?: string[]
  warehouseLocations?: string[]
  stateLocations?: string[]
  conditions?: string[]
  categories?: string[]
  subcategories?: string[]
}

type ItemSearchDataLayerParams = {
  'data-item-search-keyword'?: string
  'data-item-search-location'?: string
  'data-item-search-warehouse-location'?: string
  'data-item-search-state-location'?: string
  'data-item-search-condition'?: string
  'data-item-search-category'?: string
  'data-item-search-subcategory'?: string
}

const KEYMAP: {
  [key in keyof ItemSearchGtmData]: keyof ItemSearchDataLayerParams
} = {
  searchKeyword: 'data-item-search-keyword',
  locations: 'data-item-search-location',
  conditions: 'data-item-search-condition',
}

const buildDataLayerConditions = (
  conditions: string | string[] | undefined,
  filterData: FilterData
) => {
  try {
    if (!conditions) {
      return undefined
    }
    if (!Array.isArray(conditions)) {
      conditions = [conditions]
    }
    if (!conditions?.length) {
      return undefined
    }
    return filterData.conditions
      .filter(({ id }) => (conditions as string[]).includes(String(id)))
      .map(({ name }) => name)
  } catch (e) {
    console.error(e)
    return undefined
  }
}

const buildDataLayerCategories = (
  categories: Record<string, string[]> | null
) => {
  try {
    if (!categories) {
      return undefined
    }
    return Object.keys(categories)
  } catch (e) {
    console.error(e)
    return undefined
  }
}

const buildDataLayerSubcategories = (
  categories: Record<string, string[]> | null
) => {
  try {
    if (!categories) {
      return undefined
    }
    return Object.values(categories).flatMap((value) => value)
  } catch (e) {
    console.error(e)
    return undefined
  }
}

const buildDataLayerStateLocations = (
  locations: string | string[] | undefined,
  filterData: FilterData
) => {
  try {
    if (!locations) {
      return undefined
    }
    if (!Array.isArray(locations)) {
      locations = [locations]
    }
    if (!(locations && locations.length)) {
      return undefined
    }
    const data = Object.entries(filterData.locations).reduce(
      (acc, [key, value]) => [...acc, { key, values: Object.keys(value) }],
      [] as Array<{ key: string; values: string[] }>
    )

    return data
      .filter((item) =>
        (locations as string[]).find((id) => item.values.includes(id))
      )
      .map((item) => item.key)
  } catch (e) {
    console.error(e)
    return undefined
  }
}

const buildDataLayerWarehouseLocations = (
  locations: string | string[] | undefined,
  filterData: FilterData
) => {
  try {
    if (!locations) {
      return undefined
    }
    if (!Array.isArray(locations)) {
      locations = [locations]
    }
    if (!locations?.length) {
      return undefined
    }
    const data = Object.values(filterData.locations).reduce(
      (acc, curr) => ({ ...acc, ...curr }),
      {}
    )
    return locations.map((value) => data[value as never])
  } catch (e) {
    console.error(e)
    return undefined
  }
}

const buildDataLayerLocations = (
  locations: string | string[] | undefined,
  filterData: FilterData
) => {
  let result: string[] = []
  try {
    if (!locations) {
      return undefined
    }
    if (!Array.isArray(locations)) {
      locations = [locations]
    }
    if (!locations?.length) {
      return undefined
    }
    Object.entries(filterData.locations).forEach(([nodeKey, node]) => {
      if (
        Object.keys(node).every((item) =>
          (locations as string[]).includes(item)
        )
      ) {
        result.push(nodeKey)
      } else {
        const filtered = Object.entries(node)
          .filter(([locationId]) =>
            (locations as string[]).includes(locationId)
          )
          .map(([locationId, locationName]) => locationName)
        result.push(...filtered)
      }
    })
  } catch (e) {
    console.error(e)
    return undefined
  }

  return result
}

const buildSearchItemData = (
  queries: ItemSearchQueries,
  filterData: FilterData
): ItemSearchGtmData => {
  const keys = Object.keys(queries) as Array<keyof ItemSearchQueries>
  const data = keys.reduce((acc, curr) => {
    if (curr === 'locations') {
      return {
        ...acc,
        locations: buildDataLayerLocations(queries.locations, filterData),
        warehouseLocations: buildDataLayerWarehouseLocations(
          queries.locations,
          filterData
        ),
        stateLocations: buildDataLayerStateLocations(
          queries.locations,
          filterData
        ),
      }
    }
    if (curr === 'itemConditionIds') {
      return {
        ...acc,
        conditions: buildDataLayerConditions(
          queries.itemConditionIds,
          filterData
        ),
      }
    }
    if (curr === 'categoryJson') {
      try {
        const categories = JSON.parse(
          decodeURIComponent(queries.categoryJson || 'null')
        )
        return {
          ...acc,
          categories: buildDataLayerCategories(categories),
          subcategories: buildDataLayerSubcategories(categories),
        }
      } catch (e) {
        console.error(e)
      }
    }
    if (curr === 'itemSearchKeywords') {
      return {
        ...acc,
        searchKeyword: queries.itemSearchKeywords,
      }
    }
    return acc
  }, {} as ItemSearchGtmData)
  return data
}

const compareArrayValues = (
  value: string[] | undefined,
  valuePrevious: string[] | undefined
): string[] | undefined => {
  if (value === undefined) {
    return []
  }
  if (valuePrevious === undefined) {
    return value.length === 1 ? value : []
  }
  const distinct = value.reduce(
    (acc, curr) => (valuePrevious.includes(curr) ? acc : [...acc, curr]),
    [] as string[]
  )
  return distinct.length === 1 ? distinct : undefined
}

const getItemSearchFirstTimeDataLayerParams = (
  data: ItemSearchGtmData
): ItemSearchDataLayerParams => {
  return {
    'data-item-search-keyword': data.searchKeyword,
    'data-item-search-location': undefined,
    'data-item-search-warehouse-location': undefined,
    'data-item-search-state-location': undefined,
    'data-item-search-condition': undefined,
    'data-item-search-category': undefined,
    'data-item-search-subcategory': undefined,
  }
}

// compare data and return changes according to the algorithm:
// raise search event if search word changed or with any filter new value checked
const getItemSearchDataLayerParams = (
  data: ItemSearchGtmData,
  dataPrevious: ItemSearchGtmData
): ItemSearchDataLayerParams => {
  const keys = Object.keys(data) as Array<keyof ItemSearchGtmData>
  const dataCompared = keys.reduce((acc, curr) => {
    if (curr === 'searchKeyword') {
      return {
        ...acc,
        searchKeyword:
          data.searchKeyword !== dataPrevious.searchKeyword
            ? data.searchKeyword
            : undefined,
      }
    }
    if (curr === 'locations') {
      return {
        ...acc,
        locations: compareArrayValues(data.locations, dataPrevious.locations),
      }
    }
    if (curr === 'warehouseLocations') {
      return {
        ...acc,
        warehouseLocations: compareArrayValues(
          data.warehouseLocations,
          dataPrevious.warehouseLocations
        ),
      }
    }
    if (curr === 'stateLocations') {
      return {
        ...acc,
        stateLocations: compareArrayValues(
          data.stateLocations,
          dataPrevious.stateLocations
        ),
      }
    }
    if (curr === 'conditions') {
      return {
        ...acc,
        conditions: compareArrayValues(
          data.conditions,
          dataPrevious.conditions
        ),
      }
    }
    if (curr === 'categories') {
      return {
        ...acc,
        categories: compareArrayValues(
          data.categories,
          dataPrevious.categories
        ),
      }
    }
    if (curr === 'subcategories') {
      return {
        ...acc,
        subcategories: compareArrayValues(
          data.subcategories,
          dataPrevious.subcategories
        ),
      }
    }
    return acc
  }, {} as ItemSearchGtmData)
  return {
    'data-item-search-keyword': dataCompared.searchKeyword,
    'data-item-search-location': dataCompared.locations?.[0],
    'data-item-search-warehouse-location': dataCompared.warehouseLocations?.[0],
    'data-item-search-state-location': dataCompared.stateLocations?.[0],
    'data-item-search-condition': dataCompared.conditions?.[0],
    'data-item-search-category': dataCompared.categories?.[0],
    'data-item-search-subcategory': dataCompared.subcategories?.[0],
  }
}

export {
  buildSearchItemData,
  getItemSearchDataLayerParams,
  getItemSearchFirstTimeDataLayerParams,
  type FilterData,
  type ItemSearchQueries,
  type ItemSearchDataLayerParams,
  type ItemSearchGtmData,
}
